import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { auth } from '../services/firebase';
import { useBrandingContext } from '../contexts/BrandingContext';
import { LanguageSwitcher } from '../components/ui';

/**
 * Branded handler for Firebase Auth email action links (password reset, email
 * verification). Replaces the unbranded default `__/auth/action` widget.
 *
 * To route the emails here, set the project's action URL in the Firebase Console
 * (Authentication → Templates → "Customize action URL") to
 * `https://<domain>/auth/action`. Firebase appends `mode`, `oobCode`, `apiKey`,
 * `continueUrl`, `lang`. We only consume `mode`, `oobCode`, `lang`.
 *
 * Security: the oobCode is single-use and server-validated by Firebase
 * (verifyPasswordResetCode / applyActionCode). We never trust the URL beyond
 * passing the opaque code back to Firebase, and we surface expired/invalid codes
 * explicitly rather than failing blank.
 */

// recoverEmail intentionally NOT handled here — it is a distinct security flow
// (revert an unauthorized email change) needing its own copy + password-reset
// follow-up. An unsupported mode falls through to a safe 'unsupported' error
// rather than masquerading as email verification. [review fix]
type Mode = 'resetPassword' | 'verifyEmail';
type Phase = 'verifying' | 'form' | 'working' | 'success' | 'error';

const SUPPORTED_LANGS = ['bg', 'de', 'en'];

export default function AuthActionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // School identity (logo + name in the brand colour) — same branded header as
  // LoginPage, so the page reads as "ours", not a generic Firebase widget.
  const { logoUrl, schoolName, schoolNameLocalized, primaryColor } = useBrandingContext();
  const displayName = schoolNameLocalized?.[i18n.language] || schoolName || '';

  const mode = params.get('mode') as Mode | null;
  const oobCode = params.get('oobCode') || '';
  const lang = params.get('lang');

  const [phase, setPhase] = useState<Phase>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Run the (single-use, side-effecting) code validation exactly once, even
  // under React 18 StrictMode double-invoke and back/forward re-mount —
  // otherwise applyActionCode runs twice and the 2nd call reports the
  // already-consumed code as "invalid" AFTER a success. [review fix]
  const startedRef = useRef(false);
  // Synchronous in-flight guard for the reset submit. `phase` is async state, so
  // two submits in the same tick both still see phase==='form'; a ref blocks the
  // second confirmPasswordReset on the single-use oobCode. [review fix]
  const submittingRef = useRef(false);

  // Render the page in the language the email was sent in.
  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  function mapError(err: unknown): string {
    const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: unknown }).code) : '';
    switch (code) {
      case 'auth/expired-action-code':
        return t('authAction.errors.expired');
      case 'auth/invalid-action-code':
      case 'auth/user-not-found':
        return t('authAction.errors.invalid');
      case 'auth/user-disabled':
        return t('authAction.errors.userDisabled');
      case 'auth/weak-password':
        return t('authAction.errors.weakPassword');
      default:
        // NEVER surface a raw Firebase string ("Firebase: Error (auth/...)")
        // on a branded, localized page — always the generic. [review fix]
        if (code) console.error('AuthAction: unmapped auth error', code);
        return t('authAction.errors.generic');
    }
  }

  // Validate the action code on mount — exactly once (startedRef), so the
  // single-use code is consumed once even under StrictMode / re-mount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      if (!oobCode || !mode) {
        setPhase('error');
        setError(t('authAction.errors.invalidLink'));
        return;
      }
      try {
        if (mode === 'resetPassword') {
          await verifyPasswordResetCode(auth, oobCode); // throws on expired/invalid
          setPhase('form');
        } else if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          setPhase('success');
        } else {
          setPhase('error');
          setError(t('authAction.errors.unsupported'));
        }
      } catch (err) {
        setPhase('error');
        setError(mapError(err));
      }
    })();
    // Runs once on mount; URL params are read at mount and never change here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return; // synchronous double-submit guard [review fix]
    setError(null);
    if (password.length < 8) {
      setError(t('authAction.errors.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('authAction.errors.passwordsDontMatch'));
      return;
    }
    submittingRef.current = true;
    setPhase('working');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPhase('success');
    } catch (err) {
      setPhase('form');
      setError(mapError(err));
    } finally {
      submittingRef.current = false;
    }
  }

  const goToLogin = () => navigate('/login');

  // Headline depends on phase + mode — neutral while verifying so an invalid
  // link never flashes "New Password" prematurely. [review fix]
  const heading =
    phase === 'verifying'
      ? t('authAction.verifyingTitle')
      : mode === 'verifyEmail'
        ? t('authAction.verifyTitle')
        : t('authAction.resetTitle');

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <LanguageSwitcher variant="auth" activeColor={primaryColor} className="mb-6" />

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={displayName}
                className="h-20 w-auto mx-auto mb-4 object-contain"
              />
            )}
            <h1
              className="text-xl sm:text-2xl font-bold leading-tight"
              style={{ color: primaryColor }}
            >
              {displayName}
            </h1>
            <p className="text-gray-700 mt-2 font-medium">{heading}</p>
          </div>

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
            >
              {error}
            </div>
          )}

          {/* Verifying the link */}
          {phase === 'verifying' && (
            <p className="text-center text-gray-600">{t('authAction.verifyingLink')}</p>
          )}

          {/* New-password form (resetPassword) */}
          {phase === 'form' && (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">{t('authAction.resetSubtitle')}</p>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('authAction.newPassword')}
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  className="input"
                  placeholder={t('authAction.newPasswordPlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('authAction.confirmPassword')}
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    setError(null);
                  }}
                  className="input"
                  placeholder={t('authAction.confirmPasswordPlaceholder')}
                />
              </div>
              <button type="submit" className="w-full btn btn-primary">
                {t('authAction.submit')}
              </button>
            </form>
          )}

          {/* Submitting the new password */}
          {phase === 'working' && (
            <p className="text-center text-gray-600">{t('authAction.submitting')}</p>
          )}

          {/* Success */}
          {phase === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                <p className="font-medium">
                  {mode === 'verifyEmail'
                    ? t('authAction.verifySuccessTitle')
                    : t('authAction.resetSuccessTitle')}
                </p>
                <p className="text-sm mt-1">
                  {mode === 'verifyEmail'
                    ? t('authAction.verifySuccessMessage')
                    : t('authAction.resetSuccessMessage')}
                </p>
              </div>
              <button onClick={goToLogin} className="w-full btn btn-primary">
                {t('authAction.backToLogin')}
              </button>
            </div>
          )}

          {/* Error — offer a path forward */}
          {phase === 'error' && (
            <div className="space-y-3">
              <button onClick={() => navigate('/reset-password')} className="w-full btn btn-primary">
                {t('authAction.requestNewLink')}
              </button>
              <button onClick={goToLogin} className="w-full btn btn-secondary">
                {t('authAction.backToLogin')}
              </button>
            </div>
          )}
        </div>

        {/* Footer — same as LoginPage */}
        <p className="text-center mt-6 text-sm text-gray-500">
          {displayName} © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
